// weekWindow.js — THE week definition: Monday-start, in the user's local
// calendar, expressed on 'YYYY-MM-DD' strings so client and server cannot
// drift (2026-10-15). Before this file the client goal resolver and the
// server goal resolver used a ROLLING trailing-7-day window while the habit
// card and the cron's habit weekCount used the Monday-start week: the same
// gym log on Saturday Oct 10 counted toward "this week" on the goal card and
// not on the habit card. One helper, every consumer:
//   - src/Scorify.jsx getGoalAutoValue (workout goals)
//   - api/lib/goal-progress.js resolveProgress (workout goals)
//   - src/lib/kaiContext.js habitWeekCount (cron habit week count)
//   - api/lib/coach-run.js computeStatus (weekly pacing)
// All arithmetic is on UTC-noon-anchored dates built from the local date
// string, so no DST or machine-timezone effect can shift a day.
// Pure ESM, no deps; imported by both src/ and api/.

/** Local 'YYYY-MM-DD' for an instant in an IANA zone (null zone → machine zone). */
export function localDateInZone(nowMs = Date.now(), zone = undefined) {
  return new Intl.DateTimeFormat('en-CA', zone ? { timeZone: zone } : {}).format(new Date(nowMs));
}

const atNoon = (ds) => new Date(`${String(ds).slice(0, 10)}T12:00:00Z`);
const toStr = (d) => d.toISOString().slice(0, 10);

/** The Monday on or before todayStr, as 'YYYY-MM-DD'. Sunday belongs to the week that started the prior Monday. */
export function localWeekStart(todayStr) {
  const d = atNoon(todayStr);
  if (isNaN(d.getTime())) return null;
  const dow = d.getUTCDay(); // 0 = Sunday
  d.setUTCDate(d.getUTCDate() - (dow === 0 ? 6 : dow - 1));
  return toStr(d);
}

/** True when dateStr falls in the current local week: Monday <= date <= today. Future-dated rows never count. */
export function inLocalWeek(dateStr, todayStr) {
  if (typeof dateStr !== 'string' || !todayStr) return false;
  const d = dateStr.slice(0, 10);
  const start = localWeekStart(todayStr);
  return !!start && d >= start && d <= String(todayStr).slice(0, 10);
}

/** Distinct local dates in the current week from a list of date strings (or rows with a `date`). */
export function weekDays(dates, todayStr) {
  const out = new Set();
  for (const x of dates || []) {
    const d = typeof x === 'string' ? x : x?.date;
    if (inLocalWeek(d, todayStr)) out.add(String(d).slice(0, 10));
  }
  return out;
}

/**
 * Where today sits in its week: dayIndex 1 (Monday) … 7 (Sunday); daysElapsed
 * = dayIndex; daysLeft = days still available INCLUDING today (7 on Monday,
 * 1 on Sunday). Pacing for a weekly count goal reads from here.
 */
export function weekPosition(todayStr) {
  const d = atNoon(todayStr);
  if (isNaN(d.getTime())) return null;
  const dow = d.getUTCDay();
  const dayIndex = dow === 0 ? 7 : dow;
  return { weekStart: localWeekStart(todayStr), dayIndex, daysElapsed: dayIndex, daysLeft: 8 - dayIndex };
}
